import { BaseDialog } from "@/lib/Components/Dialog/BaseDialog";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { CityInput } from "@/app/Cities/Cities/Components/CityInput";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { proDriverRecordQuery } from "@/app/ProDrivers/Drivers/GraphQL/proDriverRecordQuery";
import { TextAreaInput } from "@/lib/Components/Form/Inputs/TextAreaInput";
import { TextInput } from "@/lib/Components/Form/Inputs/TextInput";
import { DateOfBirthInput } from "@/lib/Components/Form/Inputs/DateOfBirthInput";
import { useDialogState } from "@/lib/Components/Dialog/Hooks/useDialogState";
import { updateProDriverMutation } from "@/app/ProDrivers/Drivers/GraphQL/proDriverMutations";
import { LicenseTypeInput } from "@/app/ProDrivers/Drivers/Components/LicenseTypeInput";
import { TaxRegistrationInput } from "@/app/ProDrivers/Drivers/Components/TaxRegistrationInput";
import { DialogSubmitButton } from "@/lib/Components/Dialog/Components/DialogSubmitButton";
import { FormDivider } from "@/lib/Components/Form/Components/FormDivider";
import { TaxRegistration, UpdateProDriverInput } from "@/gql/graphql";
import { useFormikContext } from "formik";

export function UpdateProDriverDialog({
  paidDriverId,
}: {
  paidDriverId: string;
}) {
  const { data } = useSuspenseGqlQuery(proDriverRecordQuery, {
    id: paidDriverId,
  });
  const { mutateAsync } = useGqlMutation(updateProDriverMutation);
  const { close } = useDialogState();

  const proDriver = data.proDriver;

  return (
    <BaseDialog title="Update Pro Driver">
      <GenericForm
        onSubmit={async (values) => {
          const input: UpdateProDriverInput = {
            name: values.name,
            phone: values.phone || null,
            date_of_birth: values.date_of_birth || null,
            license_type: values.license_type,
            license_number: values.license_number || null,
            license_issuer: values.license_issuer || null,
            bio: values.bio,
            basedIn: {
              connect: values.basedIn,
            },
            tax_registration: values.tax_registration,
            business_registration_number:
              values.business_registration_number || null,
            bank_name: values.bank_name || null,
            bank_account_name: values.bank_account_name || null,
            bank_account_number: values.bank_account_number || null,
            bank_identifier_code: values.bank_identifier_code || null,
          };

          await mutateAsync({
            id: paidDriverId,
            input,
          });
          await close();
        }}
        initialValues={{
          name: proDriver.name,
          email: proDriver.email ?? "",
          phone: proDriver.phone ?? "",
          date_of_birth: proDriver.date_of_birth ?? "",
          basedIn: proDriver.basedIn.id,
          bio: proDriver.bio,
          license_type: proDriver.license_type,
          license_number: proDriver.license_number ?? "",
          license_issuer: proDriver.license_issuer ?? "",
          tax_registration: proDriver.tax_registration,
          business_registration_number:
            proDriver.business_registration_number ?? "",
          bank_name: proDriver.bank_name ?? "",
          bank_account_name: proDriver.bank_account_name ?? "",
          bank_account_number: proDriver.bank_account_number ?? "",
          bank_identifier_code: proDriver.bank_identifier_code ?? "",
        }}
      >
        <UpdateProDriverFormFields />
        <DialogSubmitButton text="Update" />
      </GenericForm>
    </BaseDialog>
  );
}

function UpdateProDriverFormFields() {
  const { values } = useFormikContext<UpdateProDriverInput>();

  return (
    <>
      <TextInput name="name" label="Name" className="col-span-full" autoFocus />
      <TextInput
        name="email"
        label="Email"
        type="email"
        isEditable={false}
        className="col-span-3"
      />
      <TextInput name="phone" label="Phone" className="col-span-3" />
      <DateOfBirthInput
        name="date_of_birth"
        label="Date of Birth"
        className="col-span-6"
      />

      <CityInput name="basedIn" label="Based In" className="col-span-3" />

      <LicenseTypeInput label="License Type" name="license_type" />
      <TextInput
        name="license_number"
        label="License Number"
        className="col-span-3"
      />
      <TextInput
        name="license_issuer"
        label="License Issuer"
        className="col-span-3"
      />

      <TaxRegistrationInput
        name="tax_registration"
        label="Tax Registration"
        className="col-span-3"
      />
      {values.tax_registration !== TaxRegistration.None ? (
        <TextInput
          name="business_registration_number"
          label="Business Registration Number"
          className="col-span-3"
        />
      ) : null}

      <TextAreaInput name="bio" label="Bio" className="col-span-full" />

      <FormDivider />

      <h3 className="col-span-full text-sm font-medium text-gray-700">
        Bank Details
      </h3>
      <TextInput name="bank_name" label="Bank Name" className="col-span-3" />
      <TextInput
        name="bank_account_name"
        label="Account Name"
        className="col-span-3"
      />
      <TextInput
        name="bank_account_number"
        label="Account Number"
        className="col-span-3"
      />
      <TextInput
        name="bank_identifier_code"
        label="Bank Identifier Code"
        className="col-span-3"
      />
    </>
  );
}
